import React, { useCallback, useEffect, useState } from 'react';

import { Spinner } from '../components/base';
import { chamar } from '../lib/firebase';

const PAPEIS = [
  { key: 'admin',  label: 'Admin',        ajuda: 'Mexe em tudo, inclusive equipe e sites.' },
  { key: 'editor', label: 'Editor',       ajuda: 'Cria e edita leads. Não apaga nem gerencia cadastros.' },
  { key: 'viewer', label: 'Visualizador', ajuda: 'Só olha.' },
];

const listarEquipe    = chamar('listarEquipe');
const convidarUsuario = chamar('convidarUsuario');
const atualizarAcesso = chamar('atualizarAcesso');

/**
 * Equipe da empresa. Toda escrita passa pelo servidor: o documento do usuário
 * é fechado para o navegador de propósito, senão quem editasse o próprio doc
 * se promoveria a admin de qualquer empresa.
 */
function EquipeView({ company, companyLabel, isAdmin, meuEmail }) {
  const [equipe, setEquipe] = useState(null);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [novo, setNovo] = useState(null); // { email, role }

  const carregar = useCallback(async () => {
    setErro('');
    try { setEquipe((await listarEquipe({ company })).equipe || []); }
    catch (e) { setEquipe([]); setErro(e.message || 'Não consegui carregar a equipe.'); }
  }, [company]);

  useEffect(() => { setEquipe(null); if (isAdmin) carregar(); }, [company, isAdmin, carregar]);

  const convidar = async () => {
    setOcupado(true); setErro(''); setAviso('');
    try {
      const r = await convidarUsuario({ company, email: novo.email, role: novo.role });
      setNovo(null);
      setAviso(r.contaCriada
        ? `Acesso criado para ${r.email}. Como é a primeira vez, peça para entrar com o Google ou usar "Esqueci minha senha" na tela de login.`
        : `${r.email} agora tem acesso a ${companyLabel || company}.`);
      await carregar();
    } catch (e) { setErro(e.message || 'Não consegui convidar.'); }
    setOcupado(false);
  };

  const mudarPapel = async (p, role) => {
    setOcupado(true); setErro(''); setAviso('');
    try { await atualizarAcesso({ company, email: p.email, acao: 'papel', role }); await carregar(); }
    catch (e) { setErro(e.message || 'Não consegui mudar o papel.'); }
    setOcupado(false);
  };

  const remover = async (p) => {
    if (!confirm(`Tirar o acesso de ${p.email} a ${companyLabel || company}?`)) return;
    setOcupado(true); setErro(''); setAviso('');
    try { await atualizarAcesso({ company, email: p.email, acao: 'remover' }); await carregar(); }
    catch (e) { setErro(e.message || 'Não consegui remover.'); }
    setOcupado(false);
  };

  const inp = 'apple-input w-full px-3 py-2 text-sm';

  if (!isAdmin) return <div className="max-w-2xl mx-auto acard rounded-2xl p-8 text-center">
    <p className="text-sm text-gray-500">Só quem é admin desta empresa vê a equipe.</p>
  </div>;

  if (equipe === null) return <div className="flex justify-center py-16"><Spinner /></div>;

  return <div className="max-w-2xl mx-auto space-y-4">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div>
        <h2 className="text-lg font-bold text-gray-800">Equipe · {companyLabel || company}</h2>
        <p className="text-xs text-gray-400 mt-0.5">Quem entra nesta empresa e o que pode fazer aqui dentro.</p>
      </div>
      {!novo && <button onClick={() => { setNovo({ email: '', role: 'editor' }); setErro(''); setAviso(''); }}
        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-sm">+ Convidar</button>}
    </div>

    {novo && <div className="acard rounded-2xl p-4 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 font-medium mb-1 block">E-mail *</label>
          <input className={inp} autoFocus type="email" placeholder="pessoa@empresa.com.br"
            value={novo.email} onChange={e => setNovo({ ...novo, email: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium mb-1 block">Papel</label>
          <select className={inp} value={novo.role} onChange={e => setNovo({ ...novo, role: e.target.value })}>
            {PAPEIS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <p className="text-[11px] text-gray-400 mt-1">{PAPEIS.find(p => p.key === novo.role)?.ajuda}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={convidar} disabled={ocupado || !novo.email.trim()}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-full">
          {ocupado ? 'Enviando...' : 'Dar acesso'}
        </button>
        <button onClick={() => { setNovo(null); setErro(''); }} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2">Cancelar</button>
      </div>
    </div>}

    {erro && <p className="text-xs text-red-600 px-1">{erro}</p>}
    {aviso && <p className="text-xs text-green-700 bg-green-50 rounded-xl px-3 py-2">{aviso}</p>}

    {equipe.map(p => <div key={p.email} className="acard rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">
          {p.email}{p.email === meuEmail && <span className="text-xs text-gray-400 font-normal"> · você</span>}
        </p>
        <p className="text-[11px] text-gray-400 mt-0.5">
          {p.active ? 'Ativo' : 'Inativo'}
          {p.empresas > 1 && ` · acessa ${p.empresas} empresas`}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <select className="apple-input px-2 py-1.5 text-xs" value={p.admin ? 'admin' : p.role}
          disabled={ocupado || p.email === meuEmail}
          onChange={e => mudarPapel(p, e.target.value)}>
          {PAPEIS.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
        <button onClick={() => remover(p)} disabled={ocupado || p.email === meuEmail}
          className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-30">Tirar acesso</button>
      </div>
    </div>)}
  </div>;
}

export { EquipeView };
